import { useEffect, useMemo, useState } from 'react';
import { getConfig } from '@edx/frontend-platform';
import { sendPageEvent, sendTrackEvent } from '@edx/frontend-platform/analytics';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Form, Spinner, StatefulButton } from '@openedx/paragon';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import Skeleton from 'react-loading-skeleton';

import ConfigurableRegistrationForm from './components/ConfigurableRegistrationForm';
import RegistrationFailure from './components/RegistrationFailure';
import { useRegistration } from './data/apiHook';
import { FORM_SUBMISSION_ERROR, TPA_AUTHENTICATION_FAILURE } from './data/constants';
import { isFormValid, prepareRegistrationPayload } from './data/utils';
import messages from './messages';
import { EmailField, NameField, UsernameField } from './RegistrationFields';
import {
  InstitutionLogistration,
  PasswordField,
  RedirectLogistration,
  ThirdPartyAuthAlert,
} from '../common-components';
import { useThirdPartyAuthContext } from '../common-components/components/ThirdPartyAuthContext';
import { useThirdPartyAuthHook } from '../common-components/data/apiHook';
import EnterpriseSSO from '../common-components/EnterpriseSSO';
import ThirdPartyAuth from '../common-components/ThirdPartyAuth';
import { COMPLETE_STATE, DEFAULT_STATE, PENDING_STATE, REGISTER_PAGE } from '../data/constants';
import {
  getAllPossibleQueryParams, getTpaHint, getTpaProvider, isHostAvailableInQueryParams, setCookie,
} from '../data/utils';
import { useRegisterContext } from './components/RegisterContext';

const normalizeChildSafelyLang = (value) => {
  if (!value) {
    return '';
  }

  const lang = String(value).trim().toLowerCase().split(/[-_]/)[0];
  return ['tr', 'en', 'de'].includes(lang) ? lang : '';
};

const readChildSafelyCookie = (name) => {
  if (typeof document === 'undefined' || !document.cookie) {
    return '';
  }

  const cookies = document.cookie.split(';').map(cookie => cookie.trim());
  const found = cookies.find(cookie => cookie.startsWith(`${name}=`));

  if (!found) {
    return '';
  }

  return decodeURIComponent(found.split('=').slice(1).join('='));
};

const getLangFromSearch = (search) => {
  if (!search) {
    return '';
  }

  const params = new URLSearchParams(search);

  return normalizeChildSafelyLang(
    params.get('lang')
    || params.get('locale')
    || params.get('language')
    || params.get('preview-lang')
    || params.get('lang_refresh'),
  );
};

// CHILDSAFELY_AUTH_LANG_V8
const getChildSafelyLang = (intlLocale) => {
  if (typeof window !== 'undefined') {
    const urlLang = getLangFromSearch(window.location.search);

    if (urlLang) {
      return urlLang;
    }
  }

  const explicitLang = normalizeChildSafelyLang(
    readChildSafelyCookie('childsafely_lang_v3'),
  );

  if (explicitLang) {
    return explicitLang;
  }

  /*
   * Do not trust legacy childsafely_lang here.
   * It may contain an old "en" value while the LMS itself
   * is already Turkish.
   *
   * For a user without a NEW explicit preference,
   * ChildSafely starts in Turkish.
   */
  return 'tr';
};

const TEACHER_TERMS_VERSION = 'v1';

const childSafelyRegisterCopy = {
  tr: {
    successTitle: 'E-Postanı Onayla',
    successBodyPrefix: 'Harika! Kaydın başarıyla oluşturuldu.',
    successBodySuffix: 'Öğrenme paneline erişebilmek için',
    successBodyEnd: 'adresine bir onay bağlantısı gönderdik. Lütfen gelen kutunu veya spam klasörünü kontrol et ve bağlantıya tıkla.',
    homeButton: 'Ana Sayfaya Dön',
    registerTitle: 'Kayıt Olun',
    registerSubtitle: 'Öğrenme yolculuğuna başlamak için rolünü seç.',
    studentRole: 'Öğrenciyim',
    teacherRole: 'Öğretmenim',
    parentRole: 'Veli / Ebeveynim',
    back: '← Geri Dön',
    studentAccountTitle: 'Hemen bir öğrenci hesabı açın!',
    studentAccountSubtitle: 'Size en iyi deneyimi sunabilmek için, öncelikle doğum tarihinizi bilmemiz lazım!',
    birthYearLabel: 'Doğum Yılın',
    birthYearPlaceholder: 'Yıl Seçin',
    continue: 'Devam Et ➔',
    parentEmailTitle: 'Velinizin e-posta adresi nedir?',
    parentEmailSubtitle: 'ChildSafely kullanmak için izne ihtiyacınız var.',
    parentEmailPlaceholder: 'Veli E-Posta Adresi',
    terms: 'Bu kutuyu işaretleyerek Hizmet Koşulları ve Gizlilik Politikasını kabul ediyorum.',
    juniorStrong: 'Harika!',
    juniorNotice: 'Veli onay mailin hesabın açılınca gönderilecek.',
    usernameJuniorHelp: 'Yalnızca harf ve sayı kullanın. Güvenliğiniz için gerçek adınızı kullanmayın!',
    parentOption: 'Ebeveynim',
    registerButton: '🚀 Maceraya Katıl',
    pending: 'Bekleniyor...',
    alreadyHaveAccount: 'Zaten hesabın var mı? Giriş Yap',
    fullNameLabel: 'Ad Soyad',
    fullNameHelp: 'Resmi adını ve soyadını yaz.',
    emailLabel: 'E-posta',
    emailHelp: 'Aktif olarak kullandığın e-posta adresini gir.',
    usernameLabel: 'Kullanıcı Adı',
    usernameHelp1: 'Harf, sayı ve alt çizgi kullanabilirsin.',
    usernameHelp2: 'Boşluk veya özel karakter kullanma.',
    passwordLabel: 'Şifre',
    teacherTermsTitle: 'Öğretmen Kullanım Taahhüdü',
    teacherTermsBody: 'ChildSafely eğitim içerikleri, öğretmenlerimizin kişisel mesleki gelişim kullanımı için sunulmaktadır. Kayıt olarak hesabımı yalnızca kendim kullanacağımı; platformdaki içerikleri izinsiz olarak kopyalamayacağımı, indirmeyeceğimi, çoğaltmayacağımı, paylaşmayacağımı, satmayacağımı veya başka bir ortamda kullanmayacağımı kabul ederim. Ayrıca platformdan otomatik yöntemlerle toplu veri veya içerik çıkarmamayı, güvenlik önlemlerini aşmaya çalışmamayı ve kullanıcı verilerini izinsiz şekilde toplamamayı taahhüt ederim.',
    teacherTermsCheckbox: 'Öğretmen Kullanım Taahhüdü’nü okudum ve kabul ediyorum.',
    teacherTermsView: 'Taahhüdü görüntüle',
    teacherTermsClose: 'Kapat',
  },
  en: {
    successTitle: 'Confirm Your Email',
    successBodyPrefix: 'Great! Your account has been created successfully.',
    successBodySuffix: 'To access your learning dashboard, we sent a confirmation link to',
    successBodyEnd: 'Please check your inbox or spam folder and click the link.',
    homeButton: 'Return to Home Page',
    registerTitle: 'Create Your Account',
    registerSubtitle: 'Choose your role to start your learning journey.',
    studentRole: 'I am a Student',
    teacherRole: 'I am a Teacher',
    parentRole: 'I am a Parent / Guardian',
    back: '← Go Back',
    studentAccountTitle: 'Create a student account!',
    studentAccountSubtitle: 'To give you the best experience, we first need to know your birth year.',
    birthYearLabel: 'Birth Year',
    birthYearPlaceholder: 'Select Year',
    continue: 'Continue ➔',
    parentEmailTitle: 'What is your parent or guardian email address?',
    parentEmailSubtitle: 'You need permission to use ChildSafely.',
    parentEmailPlaceholder: 'Parent / Guardian Email Address',
    terms: 'By checking this box, I accept the Terms of Service and Privacy Policy.',
    juniorStrong: 'Great!',
    juniorNotice: 'The parent approval email will be sent after your account is created.',
    usernameJuniorHelp: 'Use only letters and numbers. For your safety, do not use your real name!',
    parentOption: 'Parent / Guardian',
    registerButton: '🚀 Join the Adventure',
    pending: 'Please wait...',
    alreadyHaveAccount: 'Already have an account? Sign in',
    fullNameLabel: 'Full Name',
    fullNameHelp: 'Enter your first and last name.',
    emailLabel: 'Email',
    emailHelp: 'Enter an email address you actively use.',
    usernameLabel: 'Public Username',
    usernameHelp1: 'You can use letters, numbers, and underscores.',
    usernameHelp2: 'Do not use spaces or special characters.',
    passwordLabel: 'Password',
    teacherTermsTitle: 'Teacher Use Commitment',
    teacherTermsBody: 'ChildSafely training content is provided for teachers’ personal professional development. By registering, I agree to use my account only myself and not to copy, download, reproduce, share, sell, or reuse platform content without permission. I also undertake not to extract content or data in bulk by automated means, attempt to bypass security measures, or collect user data without authorization.',
    teacherTermsCheckbox: 'I have read and accept the Teacher Use Commitment.',
    teacherTermsView: 'View commitment',
    teacherTermsClose: 'Close',
  },
  de: {
    successTitle: 'E-Mail bestätigen',
    successBodyPrefix: 'Super! Dein Konto wurde erfolgreich erstellt.',
    successBodySuffix: 'Um auf dein Lern-Dashboard zuzugreifen, haben wir einen Bestätigungslink an',
    successBodyEnd: 'gesendet. Bitte prüfe deinen Posteingang oder Spam-Ordner und klicke auf den Link.',
    homeButton: 'Zur Startseite',
    registerTitle: 'Konto erstellen',
    registerSubtitle: 'Wähle deine Rolle, um deine Lernreise zu starten.',
    studentRole: 'Ich bin Schüler/in',
    teacherRole: 'Ich bin Lehrkraft',
    parentRole: 'Ich bin Elternteil / Erziehungsberechtigte/r',
    back: '← Zurück',
    studentAccountTitle: 'Erstelle ein Schülerkonto!',
    studentAccountSubtitle: 'Damit wir dir die beste Erfahrung bieten können, benötigen wir zuerst dein Geburtsjahr.',
    birthYearLabel: 'Geburtsjahr',
    birthYearPlaceholder: 'Jahr auswählen',
    continue: 'Weiter ➔',
    parentEmailTitle: 'Wie lautet die E-Mail-Adresse deiner Eltern oder Erziehungsberechtigten?',
    parentEmailSubtitle: 'Du brauchst eine Erlaubnis, um ChildSafely zu nutzen.',
    parentEmailPlaceholder: 'E-Mail der Eltern / Erziehungsberechtigten',
    terms: 'Mit dem Aktivieren dieses Feldes akzeptiere ich die Nutzungsbedingungen und die Datenschutzrichtlinie.',
    juniorStrong: 'Super!',
    juniorNotice: 'Die E-Mail zur Elternfreigabe wird nach der Kontoerstellung gesendet.',
    usernameJuniorHelp: 'Verwende nur Buchstaben und Zahlen. Nutze zu deiner Sicherheit nicht deinen echten Namen!',
    parentOption: 'Elternteil / Erziehungsberechtigte/r',
    registerButton: '🚀 Abenteuer starten',
    pending: 'Bitte warten...',
    alreadyHaveAccount: 'Du hast bereits ein Konto? Einloggen',
    fullNameLabel: 'Vollständiger Name',
    fullNameHelp: 'Gib deinen Vor- und Nachnamen ein.',
    emailLabel: 'E-Mail',
    emailHelp: 'Gib eine E-Mail-Adresse ein, die du aktiv nutzt.',
    usernameLabel: 'Öffentlicher Benutzername',
    usernameHelp1: 'Du kannst Buchstaben, Zahlen und Unterstriche verwenden.',
    usernameHelp2: 'Verwende keine Leerzeichen oder Sonderzeichen.',
    passwordLabel: 'Passwort',
    teacherTermsTitle: 'Nutzungsvereinbarung für Lehrkräfte',
    teacherTermsBody: 'Die Schulungsinhalte von ChildSafely werden Lehrkräften zur persönlichen beruflichen Weiterbildung bereitgestellt. Mit der Registrierung verpflichte ich mich, mein Konto nur selbst zu verwenden und Inhalte der Plattform ohne Erlaubnis weder zu kopieren, herunterzuladen, zu vervielfältigen, weiterzugeben, zu verkaufen noch anderweitig zu verwenden. Außerdem verpflichte ich mich, keine Inhalte oder Daten automatisiert in größerem Umfang auszulesen, Sicherheitsmaßnahmen nicht zu umgehen und keine Nutzerdaten unbefugt zu erfassen.',
    teacherTermsCheckbox: 'Ich habe die Nutzungsvereinbarung für Lehrkräfte gelesen und akzeptiere sie.',
    teacherTermsView: 'Vereinbarung anzeigen',
    teacherTermsClose: 'Schließen',
  },
};

const RegistrationPage = (props) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const {
    fieldDescriptions, optionalFields, thirdPartyAuthApiStatus, thirdPartyAuthContext,
    setThirdPartyAuthContextBegin, setThirdPartyAuthContextSuccess, setThirdPartyAuthContextFailure,
  } = useThirdPartyAuthContext();

  const {
    autoSubmitRegForm, currentProvider, finishAuthUrl, pipelineUserDetails, providers,
    secondaryProviders, errorMessage: thirdPartyAuthErrorMessage,
  } = thirdPartyAuthContext;

  const {
    clearRegistrationBackendError, registrationFormData, registrationResult, registrationError,
    setEmailSuggestionContext, updateRegistrationFormData, setRegistrationError, setRegistrationResult,
    backendValidations, setBackendCountryCode,
  } = useRegisterContext();

  const registrationEmbedded = isHostAvailableInQueryParams();
  const platformName = getConfig().SITE_NAME;
  const flags = {
    showConfigurableEdxFields: getConfig().SHOW_CONFIGURABLE_EDX_FIELDS,
    showConfigurableRegistrationFields: getConfig().ENABLE_DYNAMIC_REGISTRATION_FIELDS,
    showMarketingEmailOptInCheckbox: getConfig().MARKETING_EMAILS_OPT_IN,
    autoGeneratedUsernameEnabled: getConfig().ENABLE_AUTO_GENERATED_USERNAME,
  };
  const { handleInstitutionLogin, institutionLogin } = props;
  const backendRegistrationError = registrationError;
  const csLang = getChildSafelyLang(intl.locale);
  if (typeof window !== 'undefined') {
    window.__CS_REGISTER_LANG__ = csLang;
  }
  const csText = childSafelyRegisterCopy[csLang] || childSafelyRegisterCopy.tr;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__CS_REGISTER_LANG__ = csLang;
    }
  }, [csLang]);
  
  // ==========================================
  // HARİKA TUZAK (TRAP) STATE'İ
  // ==========================================
  const [showSuccessTrap, setShowSuccessTrap] = useState(false);

  const registrationMutation = useRegistration({
    onSuccess: (data) => {
      if (!currentProvider) {
        setShowSuccessTrap(true);
      } else {
        setRegistrationResult(data);
      }
      setRegistrationError({});
    },
    onError: (errorData) => {
      setRegistrationError(errorData);
    },
  });

  const [userPipelineDataLoaded, setUserPipelineDataLoaded] = useState(false);
  const registrationErrorCode = registrationError?.errorCode || backendRegistrationError?.errorCode;
  const submitState = registrationMutation.isPending ? PENDING_STATE : DEFAULT_STATE;
  const queryParams = useMemo(() => getAllPossibleQueryParams(), []);
  const tpaHint = useMemo(() => getTpaHint(), []);
  const backedUpFormData = registrationFormData;
  const [formFields, setFormFields] = useState({ ...backedUpFormData.formFields });
  const [configurableFormFields, setConfigurableFormFields] = useState({ ...backedUpFormData.configurableFormFields });
  const [errors, setErrors] = useState({ ...backedUpFormData.errors });
  const [errorCode, setErrorCode] = useState({ type: '', count: 0 });
  const [formStartTime, setFormStartTime] = useState(null);
  const [temporaryErrors, setTemporaryErrors] = useState({ ...backedUpFormData.errors });
  const { cta, host } = queryParams;

  // ==========================================
  // CHILDSAFELY ÖZEL SİHİRBAZ STATE'LERİ
  // ==========================================
  const [regStep, setRegStep] = useState(1);
  const [birthYear, setBirthYear] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [teacherTermsAccepted, setTeacherTermsAccepted] = useState(false);
  const [teacherTermsModalOpen, setTeacherTermsModalOpen] = useState(false);
  const [isJunior, setIsJunior] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  const handleRoleSelect = (role) => {
    handleOnChange({ target: { name: 'goals', value: role, type: 'text' } });
    setTeacherTermsModalOpen(false);

    if (role === 'Öğrenci') {
      setTeacherTermsAccepted(false);
      setRegStep(2);
      return;
    }

    if (role === 'Öğretmen') {
      // Her yeni öğretmen seçiminde aktif onay yeniden istenir.
      setTeacherTermsAccepted(false);
      setRegStep(4);
      return;
    }

    setTeacherTermsAccepted(false);
    setRegStep(4);
  };

  const handleAgeSubmit = () => {
    if (!birthYear) return;
    const age = currentYear - parseInt(birthYear, 10);
    if (age < 15) {
      setIsJunior(true);
      setRegStep(3); 
    } else {
      setIsJunior(false);
      setRegStep(4); 
    }
  };

  const handleParentSubmit = () => {
    if (!termsAccepted || !parentEmail) return;
    setConfigurableFormFields(prev => ({ ...prev, parent_email: parentEmail }));
    setFormFields(prev => ({ ...prev, email: parentEmail }));
    setRegStep(4);
  };
  
  const handleSmartBack = () => {
    if (formFields.goals === 'Öğretmen') {
      setTeacherTermsModalOpen(false);
      setRegStep(1);
    } else if (formFields.goals === 'Ebeveyn') {
      setRegStep(1);
    } else if (formFields.goals === 'Öğrenci') {
      if (isJunior) {
        setRegStep(3);
      } else {
        setRegStep(2);
      }
    } else {
      setRegStep(1);
    }
  };
  // ==========================================

  useEffect(() => {
    if (!userPipelineDataLoaded && thirdPartyAuthApiStatus === COMPLETE_STATE) {
      if (thirdPartyAuthErrorMessage) {
        setErrorCode(prevState => ({ type: TPA_AUTHENTICATION_FAILURE, count: prevState.count + 1 }));
      }
      if (pipelineUserDetails && Object.keys(pipelineUserDetails).length !== 0) {
        const { name = '', username = '', email = '' } = pipelineUserDetails;
        setFormFields(prevState => ({ ...prevState, name, username, email }));
        setUserPipelineDataLoaded(true);
      }
    }
  }, [thirdPartyAuthApiStatus, thirdPartyAuthErrorMessage, pipelineUserDetails, userPipelineDataLoaded]);

  const params = { ...queryParams, is_register_page: true };
  if (tpaHint) { params.tpa_hint = tpaHint; }
  const { data, isSuccess, error } = useThirdPartyAuthHook(REGISTER_PAGE, params);
  
  useEffect(() => {
    if (!formStartTime) {
      sendPageEvent('login_and_registration', 'register');
      setThirdPartyAuthContextBegin();
      setFormStartTime(Date.now());
    }
    if (formStartTime) {
      if (isSuccess && data) {
        setThirdPartyAuthContextSuccess(data.fieldDescriptions, data.optionalFields, data.thirdPartyAuthContext);
        setBackendCountryCode(data.thirdPartyAuthContext.countryCode);
      }
      if (error) { setThirdPartyAuthContextFailure(); }
    }
  }, [formStartTime, isSuccess, data, error, setThirdPartyAuthContextBegin, setThirdPartyAuthContextSuccess, setBackendCountryCode, setThirdPartyAuthContextFailure]);

  useEffect(() => {
    if (backendValidations) {
      if (registrationEmbedded) {
        setTemporaryErrors(prevErrors => ({ ...prevErrors, ...backendValidations }));
      } else {
        setErrors(prevErrors => ({ ...prevErrors, ...backendValidations }));
      }
    }
  }, [backendValidations, registrationEmbedded]);

  useEffect(() => {
    if (registrationErrorCode) {
      setErrorCode(prevState => ({ type: registrationErrorCode, count: prevState.count + 1 }));
    }
  }, [registrationErrorCode]);

  useEffect(() => {
    if (registrationResult.success) {
      sendTrackEvent('edx.bi.user.account.registered.client', {});
      setCookie(getConfig().USER_RETENTION_COOKIE_NAME, true);
    }
  }, [registrationResult]);

  const handleOnChange = (event) => {
    const { name } = event.target;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (backendRegistrationError[name]) { clearRegistrationBackendError(name); }
    if (registrationError.errorCode) { setRegistrationError({}); }
    setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    const newFormFields = { ...formFields, [name]: value };
    setFormFields(newFormFields);
    updateRegistrationFormData({ formFields: newFormFields, errors, configurableFormFields });
  };

  const handleErrorChange = (fieldName, errorMessage) => {
    if (registrationEmbedded) {
      setTemporaryErrors(prevErrors => ({ ...prevErrors, [fieldName]: errorMessage }));
      if (errorMessage === '' && errors[fieldName] !== '') {
        setErrors(prevErrors => ({ ...prevErrors, [fieldName]: errorMessage }));
      }
    } else {
      setErrors(prevErrors => ({ ...prevErrors, [fieldName]: errorMessage }));
    }
  };

  const registerUser = () => {
    if (formFields.goals === 'Öğretmen' && !teacherTermsAccepted) {
      // UI butonu disabled olsa da ikinci bir güvenlik katmanı olarak
      // submit fonksiyonundan da öğretmen kaydını engelle.
      return;
    }

    const totalRegistrationTime = (Date.now() - formStartTime) / 1000;
    let payload = { ...formFields };

    if (formFields.goals === 'Öğretmen') {
      payload.goals = `Öğretmen | CS_TEACHER_TERMS:${TEACHER_TERMS_VERSION}`;
    }

    if (currentProvider) {
      delete payload.password;
      payload.social_auth_provider = currentProvider;
    }
    if (flags.autoGeneratedUsernameEnabled) { delete payload.username; }
    
    const { isValid, fieldErrors, emailSuggestion } = isFormValid(
      payload, registrationEmbedded ? temporaryErrors : errors, configurableFormFields, fieldDescriptions, formatMessage,
    );
    setErrors({ ...fieldErrors });
    updateRegistrationFormData({ formFields, errors: fieldErrors, configurableFormFields });
    setEmailSuggestionContext(emailSuggestion.suggestion, emailSuggestion.type);

    if (!isValid) {
      setErrorCode(prevState => ({ type: FORM_SUBMISSION_ERROR, count: prevState.count + 1 }));
      return;
    }

    payload = prepareRegistrationPayload(payload, configurableFormFields, flags.showMarketingEmailOptInCheckbox, totalRegistrationTime, queryParams);
    registrationMutation.mutate(payload);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    registerUser();
  };

  useEffect(() => {
    if (autoSubmitRegForm && userPipelineDataLoaded) { registerUser(); }
  }, [autoSubmitRegForm, userPipelineDataLoaded]);

  const renderForm = () => {
    if (institutionLogin) {
      return <InstitutionLogistration secondaryProviders={secondaryProviders} headingTitle={formatMessage(messages['register.institution.login.page.title'])} />;
    }

    return (
      <div className="childsafely-register-container w-100 d-flex justify-content-center align-items-center">
        <Helmet><title>{formatMessage(messages['register.page.title'], { siteName: getConfig().SITE_NAME })}</title></Helmet>
        <CustomFormStyles />
        
        {/* Yönlendirme Mekanizması */}
        {!showSuccessTrap && (
           <RedirectLogistration host={host} authenticatedUser={registrationResult.authenticatedUser} success={registrationResult.success} redirectUrl={registrationResult.redirectUrl} finishAuthUrl={finishAuthUrl} optionalFields={optionalFields} registrationEmbedded={registrationEmbedded} redirectToProgressiveProfilingPage={ getConfig().ENABLE_PROGRESSIVE_PROFILING_ON_AUTHN && !!Object.keys(optionalFields.fields).length } />
        )}

        {/* Mükemmel Ortalı Beyaz Kartımız */}
        <div className={classNames('childsafely-card-box', { 'w-100 m-auto pt-4 main-content': registrationEmbedded })}>
            
            {/* LOGOMUZ BURADA PARLAYACAK */}
            <div className="text-center mb-4">
                <a href={getConfig().LMS_BASE_URL}>
                    <img 
                        src="https://www.childsafely.com/wp-content/uploads/2026/02/child-safely-logo-1.png" 
                        alt="ChildSafely Logo" 
                        style={{ height: '60px', margin: '0 auto', display: 'block', transition: 'transform 0.3s' }}
                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                </a>
            </div>

            {/* BAŞARI EKRANI */}
            {showSuccessTrap ? (
              <div className="text-center">
                  <div style={{ fontSize: '4.5rem', marginBottom: '20px' }}>✉️</div>
                  <h3 style={{ fontWeight: 800, fontSize: '2rem', color: '#0f172a', marginBottom: '15px' }}>{csText.successTitle}</h3>
                  <p style={{ color: '#475569', fontSize: '1.15rem', lineHeight: 1.6, marginBottom: '30px' }}>
                      {csText.successBodyPrefix}<br/><br/>
                      {csText.successBodySuffix} <strong>{formFields.email}</strong> {csText.successBodyEnd}
                  </p>
                  <a href={`${getConfig().LMS_BASE_URL}/logout`} style={{ display: 'inline-block', padding: '15px 40px', borderRadius: '50px', background: '#0ea5e9', color: 'white', fontWeight: 700, fontSize: '1.1rem', textDecoration: 'none', boxShadow: '0 10px 20px rgba(14, 165, 233, 0.3)', transition: 'all 0.3s' }}>
                      {csText.homeButton}
                  </a>
              </div>
            ) : (
              <>
                {/* ADIM 1: ROL SEÇİMİ */}
                {regStep === 1 && (
                  <div className="text-center w-100">
                    <h3 style={{ fontWeight: 800, fontSize: '1.8rem', marginBottom: '10px', color: '#0f172a' }}>{csText.registerTitle}</h3>
                    <p style={{ color: '#64748b', marginBottom: '30px' }}>{csText.registerSubtitle}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <button className="cs-role-btn" type="button" onClick={() => handleRoleSelect('Öğrenci')}>
                          <span className="emoji-icon">🎓</span> {csText.studentRole}
                      </button>
                      <button className="cs-role-btn" type="button" onClick={() => handleRoleSelect('Öğretmen')}>
                          <span className="emoji-icon">👨‍🏫</span> {csText.teacherRole}
                      </button>
                      <button className="cs-role-btn" type="button" onClick={() => handleRoleSelect('Ebeveyn')}>
                          <span className="emoji-icon">👪</span> {csText.parentRole}
                      </button>
                    </div>
                  </div>
                )}

                {/* ADIM 2: YAŞ DOĞRULAMA */}
                {regStep === 2 && (
                  <div className="w-100">
                    <button className="cs-back-btn" type="button" onClick={() => setRegStep(1)}>{csText.back}</button>
                    <h3 style={{ fontWeight: 800, fontSize: '1.5rem', marginBottom: '10px', color: '#0f172a' }}>{csText.studentAccountTitle}</h3>
                    <p style={{ color: '#64748b', marginBottom: '25px', lineHeight: 1.6 }}>{csText.studentAccountSubtitle}</p>
                    
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontWeight: 700, marginBottom: '10px', color: '#0f172a' }}>{csText.birthYearLabel}</label>
                      <select value={birthYear} onChange={(e) => setBirthYear(e.target.value)} style={{ width: '100%', padding: '15px', borderRadius: '12px', border: '2px solid #e2e8f0', outline: 'none', fontSize: '1rem', background: '#f8fafc', color: '#0f172a', fontWeight: '600' }}>
                        <option value="" disabled>{csText.birthYearPlaceholder}</option>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                    <button type="button" onClick={handleAgeSubmit} disabled={!birthYear} className={`cs-next-btn ${birthYear ? 'active' : ''}`}>{csText.continue}</button>
                  </div>
                )}

                {/* ADIM 3: VELİ İZNİ */}
                {regStep === 3 && (
                  <div className="w-100">
                    <button className="cs-back-btn" type="button" onClick={() => setRegStep(2)}>{csText.back}</button>
                    <h3 style={{ fontWeight: 800, fontSize: '1.5rem', marginBottom: '10px', color: '#0f172a' }}>{csText.parentEmailTitle}</h3>
                    <p style={{ color: '#64748b', marginBottom: '25px', lineHeight: 1.6 }}>{csText.parentEmailSubtitle}</p>
                    
                    <div style={{ marginBottom: '20px' }}>
                      <input type="email" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} placeholder={csText.parentEmailPlaceholder} style={{ width: '100%', padding: '15px', borderRadius: '12px', border: '2px solid #e2e8f0', outline: 'none', fontSize: '1rem', background: '#f8fafc', color: '#0f172a', fontWeight: '500' }} />
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '30px' }}>
                      <input type="checkbox" id="terms" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} style={{ marginTop: '5px', transform: 'scale(1.2)', accentColor: '#16a34a' }} />
                      <label htmlFor="terms" style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.5, cursor: 'pointer', fontWeight: '500' }}>{csText.terms}</label>
                    </div>

                    <button type="button" onClick={handleParentSubmit} disabled={!termsAccepted || !parentEmail} className={`cs-next-btn ${(termsAccepted && parentEmail) ? 'active' : ''}`}>{csText.continue}</button>
                  </div>
                )}

                {/* ADIM 4: GERÇEK EDX KAYIT FORMU */}
                {regStep === 4 && (
                  <div className="childsafely-custom-form w-100">
                    <button className="cs-back-btn" type="button" onClick={handleSmartBack}>{csText.back}</button>

                    <ThirdPartyAuthAlert currentProvider={currentProvider} platformName={platformName} referrer={REGISTER_PAGE} />
                    <RegistrationFailure errorCode={errorCode.type} failureCount={errorCode.count} context={{ provider: currentProvider, errorMessage: thirdPartyAuthErrorMessage }} />
                    
                    {isJunior && (
                       <div style={{ background: '#f0fdf4', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #bbf7d0', color: '#166534' }}>
                           <span style={{ fontSize: '1.2rem' }}>👋</span> <strong>{csText.juniorStrong}</strong> {csText.juniorNotice}
                       </div>
                    )}

                    <Form id="registration-form" name="registration-form">
                      <NameField name="name" value={formFields.name} shouldFetchUsernameSuggestions={!formFields.username.trim()} handleChange={handleOnChange} handleErrorChange={handleErrorChange} errorMessage={errors.name} helpText={[csText.fullNameHelp]} floatingLabel={csText.fullNameLabel} />
                      
                      <div style={{ display: isJunior ? 'none' : 'block' }}>
                        <EmailField name="email" value={formFields.email} confirmEmailValue={configurableFormFields?.confirm_email} handleErrorChange={handleErrorChange} handleChange={handleOnChange} errorMessage={errors.email} helpText={[csText.emailHelp]} floatingLabel={csText.emailLabel} />
                      </div>
                      
                      {!flags.autoGeneratedUsernameEnabled && (
                        <UsernameField name="username" spellCheck="false" value={formFields.username} handleChange={handleOnChange} handleErrorChange={handleErrorChange} errorMessage={errors.username} helpText={isJunior ? [csText.usernameJuniorHelp] : [csText.usernameHelp1, csText.usernameHelp2]} floatingLabel={csText.usernameLabel} />
                      )}
                      
                      {!currentProvider && (
                        <PasswordField name="password" value={formFields.password} handleChange={handleOnChange} handleErrorChange={handleErrorChange} errorMessage={errors.password} floatingLabel={csText.passwordLabel} />
                      )}

                      <div style={{ display: 'none' }}>
                        <select name="goals" className={`form-control ${formFields.goals ? 'has-value' : ''}`} value={formFields.goals || ''} onChange={handleOnChange} required>
                          <option value="" disabled hidden></option>
                          <option value="Öğrenci">{csText.studentRole}</option>
                          <option value="Öğretmen">{csText.teacherRole}</option>
                          <option value="Ebeveyn">{csText.parentOption}</option>
                        </select>
                      </div>

                      <ConfigurableRegistrationForm email={formFields.email} fieldErrors={errors} formFields={configurableFormFields} setFieldErrors={registrationEmbedded ? setTemporaryErrors : setErrors} setFormFields={setConfigurableFormFields} autoSubmitRegisterForm={autoSubmitRegForm} fieldDescriptions={fieldDescriptions} />

                      {formFields.goals === 'Öğretmen' && (
                        <div className="cs-teacher-terms-inline">
                          <label className="cs-teacher-terms-inline-check" htmlFor="teacher-terms-accepted">
                            <input
                              id="teacher-terms-accepted"
                              name="teacher_terms_accepted"
                              type="checkbox"
                              required
                              aria-required="true"
                              checked={teacherTermsAccepted}
                              onChange={(e) => setTeacherTermsAccepted(e.target.checked)}
                            />
                            <span>{csText.teacherTermsCheckbox}</span>
                          </label>

                          <button
                            type="button"
                            className="cs-teacher-terms-view-btn"
                            onClick={() => setTeacherTermsModalOpen(true)}
                          >
                            {csText.teacherTermsView}
                          </button>
                        </div>
                      )}
                      
                      <StatefulButton
                        id="register-user"
                        name="register-user"
                        type="submit"
                        variant="brand"
                        className="register-button mt-4 mb-2 w-100"
                        style={{ padding: '16px', borderRadius: '50px', fontWeight: 800, fontSize: '1.15rem' }}
                        state={submitState}
                        labels={{ default: csText.registerButton, pending: csText.pending }}
                        disabled={formFields.goals === 'Öğretmen' && !teacherTermsAccepted}
                        aria-disabled={formFields.goals === 'Öğretmen' && !teacherTermsAccepted}
                        onClick={handleSubmit}
                        onMouseDown={(e) => e.preventDefault()}
                      />
                      
                      <div className="text-center mt-3">
                          <a href={`/authn/login?next=%2F&lang=${csLang}`} style={{ color: '#0ea5e9', fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem' }}>{csText.alreadyHaveAccount}</a>
                      </div>

                      {!registrationEmbedded && (
                        <ThirdPartyAuth currentProvider={currentProvider} providers={providers} secondaryProviders={secondaryProviders} handleInstitutionLogin={handleInstitutionLogin} thirdPartyAuthApiStatus={thirdPartyAuthApiStatus} />
                      )}
                    </Form>
                  </div>
                )}
              </>
            )}
        </div>

        {teacherTermsModalOpen && (
          <div
            className="cs-teacher-terms-modal-overlay"
            role="presentation"
            onMouseDown={() => setTeacherTermsModalOpen(false)}
          >
            <div
              className="cs-teacher-terms-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cs-teacher-terms-modal-title"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="cs-teacher-terms-modal-x"
                aria-label={csText.teacherTermsClose}
                onClick={() => setTeacherTermsModalOpen(false)}
              >
                ×
              </button>

              <h3 id="cs-teacher-terms-modal-title" className="cs-teacher-terms-modal-title">
                {csText.teacherTermsTitle}
              </h3>
              <p className="cs-teacher-terms-modal-body">
                {csText.teacherTermsBody}
              </p>

              <div className="cs-teacher-terms-modal-footer">
                <button
                  type="button"
                  className="cs-teacher-terms-modal-close"
                  onClick={() => setTeacherTermsModalOpen(false)}
                >
                  {csText.teacherTermsClose}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (tpaHint) {
    if (thirdPartyAuthApiStatus === PENDING_STATE) { return <Skeleton height={36} />; }
    const { provider, skipHintedLogin } = getTpaProvider(tpaHint, providers, secondaryProviders);
    if (skipHintedLogin) {
      window.location.href = getConfig().LMS_BASE_URL + provider.registerUrl;
      return null;
    }
    return provider ? <EnterpriseSSO provider={provider} /> : renderForm();
  }
  return renderForm();
};

RegistrationPage.propTypes = {
  institutionLogin: PropTypes.bool,
  handleInstitutionLogin: PropTypes.func,
};

RegistrationPage.defaultProps = {
  handleInstitutionLogin: null,
  institutionLogin: false,
};

const CustomFormStyles = () => (
  <style>
    {`
      /* PARAGON'UN SOL SIKICI PANELİNİ GİZLER (MUCİZE BURADA BAŞLAR) */
      .pgn__auth-sidebar { display: none !important; }
      .pgn__auth-header { display: none !important; }
      
      /* SAĞ PANELİ TAM EKRAN VE MERKEZE YAYAR */
      .pgn__auth-main {
        flex: 0 0 100% !important;
        max-width: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        background-color: #f8fafc !important;
        background-image: radial-gradient(#cbd5e1 2px, transparent 2px) !important;
        background-size: 40px 40px !important;
        min-height: 100vh !important;
      }

      /* ANA KONTEYNER */
      .childsafely-register-container {
        width: 100% !important;
        padding: 20px !important;
      }
      
      /* YUVARLAK HATLI MÜKEMMEL BEYAZ KART */
      .childsafely-card-box {
        width: 100% !important;
        max-width: 480px !important;
        background: white !important;
        padding: 40px 30px !important;
        border-radius: 28px !important;
        box-shadow: 0 20px 50px -10px rgba(0,0,0,0.1) !important;
        margin: 0 auto !important; 
        border: 1px solid rgba(0,0,0,0.03) !important;
      }

      @media (max-width: 768px) {
        .childsafely-card-box {
          padding: 30px 20px !important;
          border-radius: 24px !important;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05) !important;
        }
      }

      /* YENİ ROL BUTONLARI (GAMIFIED) */
      .cs-role-btn {
        padding: 18px 20px !important;
        border-radius: 16px !important;
        border: 2px solid #e2e8f0 !important;
        background: #ffffff !important;
        font-size: 1.2rem !important;
        font-weight: 700 !important;
        color: #0f172a !important;
        cursor: pointer !important;
        text-align: left !important;
        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
        display: flex !important;
        align-items: center !important;
        box-shadow: 0 4px 6px rgba(0,0,0,0.02) !important;
      }
      .cs-role-btn .emoji-icon {
        font-size: 1.6rem !important;
        margin-right: 15px !important;
      }
      .cs-role-btn:hover {
        border-color: #0ea5e9 !important;
        background: #f0f9ff !important;
        transform: translateY(-4px) !important;
        box-shadow: 0 10px 20px rgba(14, 165, 233, 0.15) !important;
        color: #0284c7 !important;
      }

      /* GERİ DÖN BUTONU */
      .cs-back-btn {
        background: none !important;
        border: none !important;
        color: #64748b !important;
        font-weight: 700 !important;
        cursor: pointer !important;
        padding: 0 !important;
        margin-bottom: 25px !important;
        display: inline-flex !important;
        align-items: center !important;
        font-size: 0.95rem !important;
        transition: color 0.2s !important;
      }
      .cs-back-btn:hover {
        color: #0f172a !important;
      }

      /* DEVAM ET BUTONU */
      .cs-next-btn {
        width: 100% !important;
        padding: 16px !important;
        border-radius: 50px !important;
        background: #cbd5e1 !important;
        color: white !important;
        border: none !important;
        font-weight: 800 !important;
        font-size: 1.15rem !important;
        cursor: not-allowed !important;
        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
      }
      .cs-next-btn.active {
        background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%) !important;
        cursor: pointer !important;
        box-shadow: 0 10px 20px rgba(22, 163, 74, 0.3) !important;
      }
      .cs-next-btn.active:hover {
        transform: translateY(-3px) !important;
        box-shadow: 0 15px 25px rgba(22, 163, 74, 0.4) !important;
      }

      /* ÖĞRETMEN KULLANIM TAAHHÜDÜ - KOMPAKT INLINE + POPUP */
      .cs-teacher-terms-inline {
        margin: 12px 0 2px !important;
        padding: 13px 14px !important;
        border: 1px solid #dbeafe !important;
        border-radius: 14px !important;
        background: #f8fbff !important;
      }
      .cs-teacher-terms-inline-check {
        display: flex !important;
        align-items: flex-start !important;
        gap: 10px !important;
        margin: 0 !important;
        color: #334155 !important;
        font-size: 0.91rem !important;
        font-weight: 650 !important;
        line-height: 1.45 !important;
        cursor: pointer !important;
      }
      .cs-teacher-terms-inline-check input {
        flex: 0 0 auto !important;
        width: 18px !important;
        height: 18px !important;
        margin: 2px 0 0 !important;
        accent-color: #16a34a !important;
        cursor: pointer !important;
      }
      .cs-teacher-terms-view-btn {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        margin: 9px 0 0 28px !important;
        padding: 7px 11px !important;
        border: 1px solid #38bdf8 !important;
        border-radius: 9px !important;
        background: #ffffff !important;
        color: #0369a1 !important;
        box-shadow: none !important;
        font-size: 0.84rem !important;
        font-weight: 800 !important;
        line-height: 1.2 !important;
        cursor: pointer !important;
        transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease !important;
      }
      .cs-teacher-terms-view-btn:hover,
      .cs-teacher-terms-view-btn:focus-visible {
        border-color: #0284c7 !important;
        background: #f0f9ff !important;
        color: #075985 !important;
      }
      .childsafely-custom-form .register-button:disabled,
      .childsafely-custom-form .register-button[aria-disabled="true"] {
        opacity: 0.48 !important;
        cursor: not-allowed !important;
        transform: none !important;
        box-shadow: none !important;
      }

      .cs-teacher-terms-modal-overlay {
        position: fixed !important;
        inset: 0 !important;
        z-index: 99999 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 20px !important;
        background: rgba(15, 23, 42, 0.56) !important;
        backdrop-filter: blur(5px) !important;
        -webkit-backdrop-filter: blur(5px) !important;
      }
      .cs-teacher-terms-modal {
        position: relative !important;
        width: min(620px, 100%) !important;
        max-height: min(78vh, 720px) !important;
        overflow-y: auto !important;
        padding: 28px 28px 24px !important;
        border: 1px solid rgba(226, 232, 240, 0.95) !important;
        border-radius: 22px !important;
        background: #ffffff !important;
        box-shadow: 0 28px 80px rgba(15, 23, 42, 0.24) !important;
      }
      .cs-teacher-terms-modal-x {
        position: absolute !important;
        top: 13px !important;
        right: 14px !important;
        width: 36px !important;
        height: 36px !important;
        padding: 0 !important;
        border: 0 !important;
        border-radius: 50% !important;
        background: #f1f5f9 !important;
        color: #334155 !important;
        font-size: 1.55rem !important;
        line-height: 1 !important;
        cursor: pointer !important;
      }
      .cs-teacher-terms-modal-title {
        margin: 0 42px 14px 0 !important;
        color: #0f172a !important;
        font-size: 1.45rem !important;
        font-weight: 850 !important;
        line-height: 1.25 !important;
      }
      .cs-teacher-terms-modal-body {
        margin: 0 !important;
        color: #475569 !important;
        font-size: 0.96rem !important;
        line-height: 1.75 !important;
      }
      .cs-teacher-terms-modal-footer {
        display: flex !important;
        justify-content: flex-end !important;
        margin-top: 22px !important;
      }
      .cs-teacher-terms-modal-close {
        padding: 9px 18px !important;
        border: 1px solid #cbd5e1 !important;
        border-radius: 10px !important;
        background: #ffffff !important;
        color: #334155 !important;
        font-weight: 800 !important;
        cursor: pointer !important;
      }

      @media (max-width: 576px) {
        .cs-teacher-terms-inline {
          padding: 12px !important;
        }
        .cs-teacher-terms-view-btn {
          margin-left: 28px !important;
          padding: 7px 10px !important;
        }
        .cs-teacher-terms-modal-overlay {
          padding: 14px !important;
          align-items: flex-end !important;
        }
        .cs-teacher-terms-modal {
          width: 100% !important;
          max-height: 82vh !important;
          padding: 24px 20px 20px !important;
          border-radius: 20px 20px 14px 14px !important;
        }
        .cs-teacher-terms-modal-title {
          font-size: 1.25rem !important;
        }
        .cs-teacher-terms-modal-body {
          font-size: 0.92rem !important;
          line-height: 1.65 !important;
        }
      }

      /* FORM ELEMANLARI (EDX OVERRIDE) */
      .childsafely-custom-form .pgn__form-group {
        margin-bottom: 1.5rem !important;
      }
      .childsafely-custom-form .pgn__form-control {
        border-radius: 14px !important;
        border: 2px solid #e2e8f0 !important;
        background-color: #f8fafc !important;
        color: #0f172a !important;
        font-weight: 600 !important;
        padding-top: 1.5rem !important;
        padding-bottom: 0.5rem !important;
        transition: all 0.3s ease !important;
      }
      .childsafely-custom-form .pgn__form-control:focus {
        border-color: #0ea5e9 !important;
        background-color: #ffffff !important;
        box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.15) !important;
      }
      .childsafely-custom-form .pgn__form-control-floating-label-text {
        color: #64748b !important;
        font-weight: 600 !important;
      }
      .childsafely-custom-form .pgn__form-control:focus ~ .pgn__form-control-floating-label .pgn__form-control-floating-label-text {
        color: #0ea5e9 !important;
      }

      /* HESABI OLUŞTUR BUTONU REVIZESI */
      .childsafely-custom-form .register-button {
        background: linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%) !important;
        border: none !important;
        box-shadow: 0 10px 25px -5px rgba(37, 99, 235, 0.4) !important;
        transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.3s ease !important;
      }
      .childsafely-custom-form .register-button:hover {
        transform: translateY(-3px) scale(1.02) !important;
        box-shadow: 0 15px 30px -5px rgba(37, 99, 235, 0.5) !important;
      }
    `}
  </style>
);

export default RegistrationPage;